import { useCallback, useMemo, useState } from 'react'
import { useEffectOnce } from 'react-use'
import {
  type ButtonGroupProps,
  type IconId,
  type ModalSingleActionProps,
  AspectRatio,
  Icon,
  ListItem,
  Modal,
  Text
} from '@stone-payments/jade'

import BlackBird from '~/domains/platform/lib/blackbird'

import { type CampaignPayload } from '../schemas/campaign'
import { campaignEvents } from '../services/analitica'

export type CampaignProps = {
  experiment: {
    screen: string
  }
  data: CampaignPayload
  onClose?: () => void
}

export const Campaign = ({ experiment, data, onClose }: CampaignProps) => {
  const [open, setOpen] = useState(true)
  const actions = useMemo(() => {
    let items: ModalSingleActionProps | ButtonGroupProps['items'] = [
      {
        text: data.actions.cta.label,
        variant: 'primary-solid',
        onClick: () => {
          campaignEvents.events.action.campaignAccepted(experiment.screen, data.campaign)

          onClose?.()

          setOpen(false)

          if (data.actions.cta.action) {
            if (data.actions.cta.action.web.external) {
              window.open(data.actions.cta.action.web.link, '_blank')

              return
            }

            BlackBird.travelTo(data.actions.cta.action.web.link)
          }
        }
      }
    ]

    if (data.actions.decline) {
      items = [
        ...items,
        {
          text: data.actions.decline.label,
          variant: 'neutral-subtle',
          onClick: () => {
            campaignEvents.events.action.campaignDeclined(experiment.screen, data.campaign)

            onClose?.()

            setOpen(false)
          }
        }
      ]
    }

    return items
  }, [data, experiment, onClose])

  const handleClose = useCallback(() => {
    campaignEvents.events.action.campaignClosed(experiment.screen, data.campaign)

    onClose?.()

    setOpen(false)
  }, [data, experiment, onClose])

  useEffectOnce(() => {
    campaignEvents.events.view.modalViewed(experiment.screen, data.campaign)
  })

  return (
    <Modal
      actions={actions}
      onClose={handleClose}
      title=""
      open={open}
      paddingless={true}
      portal={true}
      toggle={setOpen}
    >
      {data.image && (
        <AspectRatio
          aspectRatio="ultrawide"
          objectFit="cover"
          objectPosition="center"
          src={data.image.web.url}
          alt={`Campanha ${data.title}`}
        />
      )}

      <div className="px-5 pt-3 flex flex-col gap-2">
        <Text variant="heading-medium">{data.title}</Text>

        {data.description && <Text variant="text-medium">{data.description}</Text>}

        {data.topics && data.topics.length > 0 && (
          <div>
            {data.topics.map(item => (
              <ListItem
                className="!p-0 !mx-0"
                content={
                  <div>
                    <Text>{item.label}</Text>
                  </div>
                }
                leading={<Icon size="medium" use={item.icon as IconId} color="brand" />}
                compact={true}
                key={item.label}
              />
            ))}
          </div>
        )}
      </div>
    </Modal>
  )
}
